import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowRight, Shield, Lock, Truck, Plane, Landmark, Cpu, Palette, Coins, Zap, Radar, Anchor, Ship, Waves, Droplets } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import WorldMap from "@/components/WorldMap";

const ARMORED_IMG = "https://images.unsplash.com/photo-1727896908560-41f75dad9d96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhcm1vcmVkJTIwdHJ1Y2t8ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85";
const VAULT_IMG = "https://images.unsplash.com/photo-1609358905581-e5381612486e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxiYW5rJTIwdmF1bHR8ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85";
const CARGO_PLANE = "https://images.unsplash.com/photo-1774698078446-59299e016718?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMGFpcnBsYW5lJTIwY2FyZ298ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85";
const GOLD_IMG = "https://images.unsplash.com/photo-1610375461246-83df859d849d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYmFyc3xlbnwwfHx8fDE3ODUyODIwODR8MA&ixlib=rb-4.1.0&q=85";
const ART_IMG = "https://images.unsplash.com/photo-1757009632210-a395ae3f53ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxhcnQlMjBwYWludGluZyUyMGdhbGxlcnl8ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85";
const TECH_IMG = "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxjb21wdXRlciUyMHNlcnZlciUyMHJvb218ZW58MHx8fHwxNzg1MjgyMDg0fDA&ixlib=rb-4.1.0&q=85";

const assets = [
  { icon: Coins, label: "Cash in Transit", desc: "Sovereign-grade movement of banknotes, precious metals liquidity, and bearer instruments.", img: null },
  { icon: Landmark, label: "Gold & Bullion", desc: "LBMA-chain custody for gold bars, coins, and diamonds across every trading center.", img: GOLD_IMG },
  { icon: Palette, label: "Fine Art", desc: "Climate-controlled crates, white-glove handling, museum-grade transit insurance.", img: ART_IMG },
  { icon: Cpu, label: "High-Tech Components", desc: "Semiconductors, lithography optics, and prototype hardware under NDA.", img: TECH_IMG },
];

export default function Home() {
  const navigate = useNavigate();
  const [tn, setTn] = useState("");

  const onTrack = (e) => {
    e.preventDefault();
    const v = (tn || "")
      .replace(/[\u00A0\s]+/g, "")
      .replace(/[\u2010-\u2015\u2212]/g, "-")
      .trim()
      .toUpperCase();
    if (v) navigate(`/track/${v}`);
  };

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={ARMORED_IMG} alt="Armored transport" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060C1F] via-[#060C1F]/85 to-[#060C1F]/40" />
          <div className="absolute inset-0 aegis-grid-bg opacity-40" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 pb-32 lg:pt-32 lg:pb-44">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <SectionLabel>A&amp;P Fast Track Services · Codename APF/2026</SectionLabel>
            <h1 className="mt-6 font-display font-black uppercase tracking-tight text-[#F0F4F8] text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
              Offshore &amp; Global<br />
              <span className="text-[#D4AF37]">High-Value</span><br />
              Logistics
            </h1>
            <p className="mt-8 max-w-xl text-[#9EABC0] text-lg leading-relaxed">
              Sovereign-class movement of currency, bullion, art, sensitive technology, and offshore assets across 84 nations —
              from platform decks to central-bank vaults. Every consignment fortified. Every nautical mile accounted for.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-request-quote"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#C5A059] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
              >
                Request Secure Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/coverage"
                data-testid="hero-view-coverage"
                className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#D4AF37]/10"
              >
                View Global Coverage
              </Link>
            </div>

            {/* Tracking pill */}
            <form
              onSubmit={onTrack}
              data-testid="hero-track-form"
              className="mt-14 flex items-stretch max-w-xl border border-[#A1AEC2]/30 bg-[#060C1F]/70 backdrop-blur-md"
            >
              <div className="px-4 flex items-center border-r border-[#A1AEC2]/20">
                <Radar className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <input
                data-testid="hero-track-input"
                value={tn}
                onChange={(e) => setTn(e.target.value)}
                placeholder="Enter tracking number  ·  APF-2026-XXXXXX"
                className="flex-1 bg-transparent px-4 py-4 font-mono text-sm text-[#F0F4F8] placeholder-[#9EABC0]/60 outline-none uppercase tracking-wider"
              />
              <button
                type="submit"
                data-testid="hero-track-submit"
                className="bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest text-xs px-6 hover:bg-[#C5A059]"
              >
                Track
              </button>
            </form>
          </motion.div>

          {/* stats corner strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#A1AEC2]/15 border border-[#A1AEC2]/15">
            {[
              ["$48B+", "Value transported / year"],
              ["84", "Sovereign jurisdictions"],
              ["47", "Command centers"],
              ["99.997%", "Delivery integrity"],
            ].map(([n, l]) => (
              <div key={l} className="bg-[#060C1F] p-6">
                <div className="font-display text-3xl lg:text-4xl font-black text-[#D4AF37]">{n}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9EABC0]">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP + AIR SECURED */}
      <section className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <SectionLabel>Air-Secured Corridor</SectionLabel>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl font-bold text-[#F0F4F8] leading-tight">
              When the ground isn&apos;t fast enough, our sky is.
            </h2>
            <p className="mt-6 text-[#9EABC0] leading-relaxed">
              A&amp;P Fast Track operates chartered Boeing 747-8F and Airbus A330-200F cargo aircraft equipped with in-flight vault
              modules, biometric hold access, and armed marshals. Cross-continental transfers close in under 18 hours,
              hand-carried from platform to vault.
            </p>
            <div className="mt-8 space-y-4">
              {[
                ["Armed flight marshals", "3-person escort per hold"],
                ["Vault-in-hold", "10-tonne certified strong module"],
                ["Continuous telemetry", "Ku-band encrypted uplink"],
              ].map(([t, d]) => (
                <div key={t} className="flex items-start gap-4 border-l border-[#D4AF37] pl-4">
                  <div>
                    <div className="font-display font-semibold text-[#F0F4F8]">{t}</div>
                    <div className="text-sm text-[#9EABC0]">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div className="relative overflow-hidden border border-[#A1AEC2]/20">
              <img src={CARGO_PLANE} alt="Cargo aircraft loading" className="w-full h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060C1F] via-[#060C1F]/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Fleet · APF-Wing-04</div>
                  <div className="font-display text-2xl text-[#F0F4F8] mt-2">18-Hour Global Air Bridge</div>
                </div>
                <div className="hidden md:flex items-center gap-2 text-[#D4AF37]">
                  <Plane className="w-5 h-5" />
                  <span className="font-mono text-xs uppercase tracking-widest">Active</span>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-[#D4AF37]/70" />
              <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-[#D4AF37]/70" />
            </div>
          </div>
        </div>
      </section>

      {/* ASSETS WE PROTECT */}
      <section className="relative bg-[#0A1024] border-y border-[#A1AEC2]/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-3xl">
            <SectionLabel>Protected Assets</SectionLabel>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl font-bold text-[#F0F4F8]">
              Four cargo classes.<br />
              One doctrine of custody.
            </h2>
            <p className="mt-6 text-[#9EABC0] max-w-xl">
              Each asset class receives its own protocol — vault temperature, handler clearance, escort tier and telemetry package.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {assets.map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group relative bg-[#12182B] border border-[#A1AEC2]/20 hover:border-[#D4AF37]/60 overflow-hidden"
                data-testid={`asset-card-${a.label.toLowerCase().replace(/\s+|&/g, '-')}`}
              >
                {a.img && (
                  <div className="relative h-40 overflow-hidden">
                    <img src={a.img} alt={a.label} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#12182B] to-transparent" />
                  </div>
                )}
                {!a.img && (
                  <div className="relative h-40 bg-gradient-to-br from-[#12182B] to-[#1A2238] flex items-center justify-center border-b border-[#A1AEC2]/10">
                    <a.icon className="w-16 h-16 text-[#D4AF37]/70" strokeWidth={1} />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <a.icon className="w-4 h-4 text-[#D4AF37]" />
                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Class 0{i + 1}</div>
                  </div>
                  <h3 className="font-display text-xl text-[#F0F4F8] font-bold">{a.label}</h3>
                  <p className="mt-3 text-sm text-[#9EABC0] leading-relaxed">{a.desc}</p>
                </div>
                <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-[#D4AF37]/0 group-hover:border-[#D4AF37]/70 transition-colors duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFSHORE SERVICES */}
      <section className="relative overflow-hidden border-y border-[#A1AEC2]/10">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1800&q=85"
            alt="Offshore platform at dusk"
            className="w-full h-full object-cover opacity-30"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-[#060C1F] via-[#060C1F]/85 to-[#060C1F]/55" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <SectionLabel>Offshore Division</SectionLabel>
              <h2 className="mt-6 font-display font-black uppercase text-4xl lg:text-5xl text-[#F0F4F8] leading-[0.98]">
                Beyond the shoreline.<br />
                <span className="text-[#D4AF37]">Beneath the surface.</span>
              </h2>
              <p className="mt-6 text-[#9EABC0] leading-relaxed max-w-xl">
                A&amp;P Fast Track&apos;s offshore division moves high-value cargo to and from oil platforms, seismic vessels,
                wind-farm construction fleets, subsea data centers and floating vaults — through gale, night, and hostile jurisdictions.
                Certified helicopter, supply-vessel, and armed-tender coverage across every major offshore basin.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                {[
                  { icon: Anchor, t: "North Sea", d: "Aberdeen · Stavanger · Rotterdam" },
                  { icon: Ship, t: "Gulf of Mexico", d: "Houston · Galveston · Ciudad del Carmen" },
                  { icon: Waves, t: "West Africa Basin", d: "Luanda · Port Harcourt · Pointe-Noire" },
                  { icon: Droplets, t: "Arabian Gulf", d: "Dubai · Doha · Dammam" },
                ].map(({ icon: Icon, t, d }) => (
                  <div key={t} className="flex items-start gap-3 border border-[#A1AEC2]/20 hover:border-[#D4AF37]/60 bg-[#0A1024]/70 p-4">
                    <Icon className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-display font-semibold text-[#F0F4F8] text-sm">{t}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#9EABC0] mt-1">{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Offshore emblem */}
            <div className="lg:col-span-6 relative">
              <div className="relative border border-[#D4AF37]/40 bg-[#0A1024]/60 backdrop-blur-md p-10 lg:p-14">
                <div className="flex items-center justify-between mb-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Offshore Emblem · 2026</div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full aegis-pulse" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#9EABC0]">Active Fleet</span>
                  </div>
                </div>

                {/* Custom SVG offshore-rig emblem */}
                <svg viewBox="0 0 400 260" className="w-full">
                  {/* Waves */}
                  <g stroke="#D4AF37" strokeOpacity="0.6" fill="none" strokeWidth="1.5">
                    <path d="M 0 190 Q 40 180 80 190 T 160 190 T 240 190 T 320 190 T 400 190" />
                    <path d="M 0 205 Q 40 195 80 205 T 160 205 T 240 205 T 320 205 T 400 205" strokeOpacity="0.35" />
                    <path d="M 0 220 Q 40 210 80 220 T 160 220 T 240 220 T 320 220 T 400 220" strokeOpacity="0.2" />
                  </g>
                  {/* Rig legs */}
                  <g stroke="#D4AF37" strokeWidth="2" fill="none">
                    <line x1="140" y1="90" x2="130" y2="200" />
                    <line x1="180" y1="90" x2="185" y2="200" />
                    <line x1="220" y1="90" x2="225" y2="200" />
                    <line x1="260" y1="90" x2="270" y2="200" />
                    {/* cross-braces */}
                    <line x1="130" y1="150" x2="185" y2="150" opacity="0.6" />
                    <line x1="185" y1="150" x2="225" y2="150" opacity="0.6" />
                    <line x1="225" y1="150" x2="270" y2="150" opacity="0.6" />
                  </g>
                  {/* Deck */}
                  <rect x="120" y="80" width="160" height="12" fill="#D4AF37" opacity="0.9" />
                  <rect x="120" y="80" width="160" height="12" fill="none" stroke="#F0F4F8" strokeWidth="0.5" />
                  {/* Derrick */}
                  <g stroke="#D4AF37" strokeWidth="1.5" fill="none">
                    <line x1="200" y1="80" x2="200" y2="20" />
                    <line x1="180" y1="80" x2="200" y2="20" />
                    <line x1="220" y1="80" x2="200" y2="20" />
                    <line x1="185" y1="60" x2="215" y2="60" />
                    <line x1="188" y1="45" x2="212" y2="45" />
                    <line x1="192" y1="30" x2="208" y2="30" />
                  </g>
                  {/* Helipad circle */}
                  <circle cx="145" cy="70" r="10" stroke="#D4AF37" strokeWidth="1.5" fill="#060C1F" />
                  <text x="145" y="73" fill="#D4AF37" fontSize="9" textAnchor="middle" fontFamily="JetBrains Mono, monospace">H</text>
                  {/* Supply vessel */}
                  <g fill="#D4AF37" opacity="0.85">
                    <path d="M 40 210 L 100 210 L 108 220 L 32 220 Z" />
                    <rect x="55" y="200" width="30" height="10" />
                    <rect x="60" y="192" width="8" height="8" opacity="0.7" />
                  </g>
                  {/* Scanning line */}
                  <line x1="50" y1="30" x2="350" y2="30" stroke="#D4AF37" strokeOpacity="0.25" strokeDasharray="4 6" />
                  <text x="50" y="20" fill="#9EABC0" fontSize="9" fontFamily="JetBrains Mono, monospace" style={{ letterSpacing: "0.15em" }}>APF·OFFSHORE·04N</text>
                </svg>

                <div className="mt-8 grid grid-cols-3 gap-4 border-t border-[#A1AEC2]/15 pt-6">
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Platforms Served</div>
                    <div className="mt-1 font-display font-black text-2xl text-[#D4AF37]">312</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Fleet Vessels</div>
                    <div className="mt-1 font-display font-black text-2xl text-[#D4AF37]">28</div>
                  </div>
                  <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Basin Coverage</div>
                    <div className="mt-1 font-display font-black text-2xl text-[#D4AF37]">14</div>
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-[#D4AF37]" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-[#D4AF37]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP TEASER */}
      <section className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            <SectionLabel>Reach</SectionLabel>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl font-bold text-[#F0F4F8]">
              84 nations. <br /><span className="text-[#D4AF37]">One flight-plan.</span>
            </h2>
            <p className="mt-6 text-[#9EABC0] leading-relaxed">
              From Zurich to Singapore, from Johannesburg to Toronto — a single control room orchestrates every leg,
              every border, every hand-off.
            </p>
            <Link
              to="/coverage"
              data-testid="home-view-map"
              className="mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37] hover:text-[#F0F4F8] border-b border-[#D4AF37] pb-1"
            >
              Open Interactive Map <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 relative p-4 border border-[#A1AEC2]/20 bg-[#0A1024]">
            <WorldMap compact />
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="relative overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-r from-[#12182B] to-[#1A2238] p-10 lg:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <SectionLabel>Deploy A&amp;P Fast Track</SectionLabel>
              <h3 className="mt-4 font-display text-3xl lg:text-4xl font-bold text-[#F0F4F8]">
                Ready to move something the world cannot afford to lose?
              </h3>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="cta-quote"
                className="bg-[#D4AF37] text-[#060C1F] font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#C5A059] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
              >
                Get Custom Quote
              </Link>
              <Link
                to="/track"
                data-testid="cta-track"
                className="border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#D4AF37]/10"
              >
                Track Shipment
              </Link>
            </div>
            <div className="absolute -right-6 -top-6 w-24 h-24 border-4 border-[#D4AF37]/20 rotate-45" />
          </div>
        </div>
      </section>
    </div>
  );
}
